// src/LandingSection.js

import React, { useEffect, useRef, useCallback, memo } from 'react';
import { motion, useMotionValue, useTransform, useSpring, animate } from 'framer-motion';
import AnimatedButton from './AnimatedButton';

// ── Static configs ────────────────────────────────────────────────────────────
const containerVariants = {
  hidden:  { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1, duration: 0.6 },
  },
};

const itemVariants = {
  hidden:  { y: 32, opacity: 0, scale: 0.98 },
  visible: { y: 0,  opacity: 1, scale: 1, transition: { type: "spring", stiffness: 350, damping: 30 } },
};

const features = [
  { emoji: "🚀", text: "Share websites, Wi-Fi, contacts, and secret messages — all in one scan." },
  { emoji: "⚡", text: "Stop typing long links. Generate beautiful QR codes instantly."          },
  { emoji: "🔒", text: "No tracking, no accounts. Your content stays completely yours."         },
  { emoji: "💰", text: "Unlimited, high-quality QR codes — always free, forever."               },
];

const PARTICLES = [
  { width: 8,  height: 8,  top: "15%", left: "10%", duration: 4,   delay: 0   },
  { width: 10, height: 10, top: "25%", left: "85%", duration: 5,   delay: 0.5 },
  { width: 6,  height: 6,  top: "60%", left: "8%",  duration: 3.5, delay: 1   },
  { width: 8,  height: 8,  top: "80%", left: "20%", duration: 5.5, delay: 2   },
];

// ── QR grid — CSS animation ───────────────────────────────────────────────────
const QrGridBackground = memo(() => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.03] dark:opacity-[0.05]"
  >
    <div
      className="absolute inset-0 grid grid-cols-8"
      style={{ gridTemplateRows: 'repeat(4, 1fr)' }}
    >
      {Array.from({ length: 32 }).map((_, i) => (
        <div
          key={i}
          className="border border-purple-500"
          style={{
            opacity:   0,
            animation: `qrCellPulse ${3 + (i % 4)}s ease-in-out ${(i * 0.08) % 3}s infinite`,
          }}
        />
      ))}
    </div>
  </div>
));
QrGridBackground.displayName = 'QrGridBackground';

// ── Particles — CSS animation ─────────────────────────────────────────────────
const ParticlesLayer = memo(() => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    {PARTICLES.map((p, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-purple-400/15 dark:bg-purple-400/10 hw-accel"
        style={{
          width:     p.width,
          height:    p.height,
          top:       p.top,
          left:      p.left,
          animation: `particleFloat ${p.duration}s ease-in-out ${p.delay}s infinite`,
        }}
      />
    ))}
  </div>
));
ParticlesLayer.displayName = 'ParticlesLayer';

// ── Animated counter ──────────────────────────────────────────────────────────
const AnimatedCounter = memo(({ target, suffix = "" }) => {
  const nodeRef = useRef(null);
  useEffect(() => {
    const node     = nodeRef.current;
    const controls = animate(0, target, {
      duration: 1.5,
      delay:    0.8,
      ease:     "easeOut",
      onUpdate: (v) => {
        if (node) node.textContent = Math.floor(v).toLocaleString() + suffix;
      },
    });
    return () => controls.stop();
  }, [target, suffix]);
  return <span ref={nodeRef}>0{suffix}</span>;
});
AnimatedCounter.displayName = 'AnimatedCounter';

// ── Headline ──────────────────────────────────────────────────────────────────
const HeadlineText = memo(() => (
  <motion.div variants={itemVariants} className="mb-6">
    <h1
      id="landing-heading"
      className="text-6xl sm:text-7xl md:text-8xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-[1.05]"
    >
      Say it with
      <br />
      <span className="relative inline-block mt-2">
        <span className="bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500 bg-clip-text text-transparent">
          your QR.
        </span>
        {/* Underline draws in on load */}
        <motion.span
          aria-hidden="true"
          className="absolute inset-x-0 -bottom-2 h-1 bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500 rounded-full"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.7, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
        {/* Subtle glow under headline — CSS only */}
        <span
          aria-hidden="true"
          className="absolute inset-x-0 -bottom-1 h-6 bg-purple-400/15 blur-lg rounded-full"
          style={{ animation: 'glowPulse 3s ease-in-out infinite' }}
        />
      </span>
    </h1>
  </motion.div>
));
HeadlineText.displayName = 'HeadlineText';

// ── Feature card — subtle lift on hover ───────────────────────────────────────
const FeatureCard = memo(({ emoji, text }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -3, transition: { duration: 0.2, ease: 'easeOut' } }}
    className="flex items-start gap-3 bg-white/80 dark:bg-[#1a1a2e]/80 border border-gray-100 dark:border-purple-500/15 rounded-xl px-4 py-3 cursor-default"
  >
    <span className="text-xl leading-none mt-0.5 flex-shrink-0">{emoji}</span>
    <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">{text}</p>
  </motion.div>
));
FeatureCard.displayName = 'FeatureCard';

// ── Stat item — number + label ─────────────────────────────────────────────────
const StatItem = memo(({ target, suffix, label }) => (
  <div className="text-center">
    <p className="text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">
      <AnimatedCounter target={target} suffix={suffix} />
    </p>
    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mt-0.5">{label}</p>
  </div>
));
StatItem.displayName = 'StatItem';

// ── Scroll hint — CSS only, no Framer ────────────────────────────────────────


// ── Badge — subtle border glow ────────────────────────────────────────────────
const Badge = memo(() => (
  <motion.div variants={itemVariants} className="flex justify-center mb-8">
    <span
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-semibold uppercase tracking-widest"
      style={{ animation: 'badgeGlow 3s ease-in-out infinite' }}
    >
      ✦ Free &amp; Open to All
    </span>
  </motion.div>
));
Badge.displayName = 'Badge';

// ── Main component ────────────────────────────────────────────────────────────
const LandingSection = memo(({ onStart }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springX = useSpring(mouseX, { stiffness: 60, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 60, damping: 30 });

  const orb1X = useTransform(springX, [-500, 500], [-20, 20]);
  const orb1Y = useTransform(springY, [-500, 500], [-20, 20]);
  const orb2X = useTransform(springX, [-500, 500], [15, -15]);
  const orb2Y = useTransform(springY, [-500, 500], [15, -15]);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width  / 2);
    mouseY.set(e.clientY - rect.top  - rect.height / 2);
  }, [mouseX, mouseY]);

  const scrollToGenerator = useCallback(() => {
    if (onStart) onStart();
  }, [onStart]);

  return (
    <motion.section
      id="landing"
      aria-labelledby="landing-heading"
      className="relative flex flex-col items-center justify-center min-h-screen bg-white dark:bg-[#0f0f1a] transition-colors duration-500 text-center px-6 py-24 overflow-hidden"
      onMouseMove={handleMouseMove}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <QrGridBackground />
      <ParticlesLayer />

      {/* Mouse-tracked orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          style={{ x: orb1X, y: orb1Y }}
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/8 dark:bg-purple-500/12 blur-[120px]"
        />
        <motion.div
          style={{ x: orb2X, y: orb2Y }}
          className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-indigo-500/8 dark:bg-indigo-500/12 blur-[100px]"
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">

        {/* Badge */}
        <Badge />

        {/* Headline */}
        <HeadlineText />

        {/* Subheadline */}
        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          The fastest way to turn any link, text, or message into a scannable QR code —
          no sign-up, no limits, no fuss.
        </motion.p>

        {/* Stats — each counter animates up on load */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-8 mb-10"
        >
          <StatItem target={100}  suffix="%"        label="Free"     />
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <StatItem target={0}    suffix=" tracking" label="Privacy"  />
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <StatItem target={1200} suffix=" chars"    label="Max Input" />
        </motion.div>

        {/* Feature cards */}
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-12 text-left"
        >
          <FeatureCard emoji={features[0].emoji} text={features[0].text} />
          <FeatureCard emoji={features[1].emoji} text={features[1].text} />
          <FeatureCard emoji={features[2].emoji} text={features[2].text} />
          <FeatureCard emoji={features[3].emoji} text={features[3].text} />
        </motion.div>

        {/* CTA */}
        <motion.div variants={itemVariants} className="flex justify-center">
          <AnimatedButton onClick={scrollToGenerator} />
        </motion.div>

        {/* Scroll hint */}
        <motion.div variants={itemVariants}>
        
        </motion.div>

      </div>
    </motion.section>
  );
});

LandingSection.displayName = 'LandingSection';
export default LandingSection;